/**
 * 이벤트 메타 자동 캡처 (Phase 1 — 온톨로지 V0).
 *
 * 두 종류:
 *   1) **세션 메타** — 첫 landing 시 1회 캡처 → sessionStorage 에 저장 → 모든 이벤트에 부착
 *      utm_*, landingReferrer, landingPath, viewport, deviceType
 *   2) **요청 메타** — 매 이벤트마다 캡처 (cheap, 2~3 µs)
 *      locale, timezone, prefersColorScheme, viewport(현재)
 *
 * 분리 이유: utm 파라미터는 attribution 추적에 중요한데 첫 landing 후엔 url 에서 사라짐.
 *           반면 viewport / locale 은 매 이벤트마다 잡는 게 디바이스 회전·언어 변경 추적에 유리.
 */

const SESSION_META_KEY = "bs:analytics:session-meta";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

export interface SessionMeta {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  landingReferrer?: string;
  landingPath?: string;
  landingTs?: number;
  /** 첫 landing 시점의 viewport (가입 funnel 분석용) */
  initialViewport?: string;
  /** 디바이스 타입 — desktop / tablet / mobile */
  deviceType?: "desktop" | "tablet" | "mobile";
}

export interface RequestMeta {
  /** "1920x1080" — 매 이벤트의 현재 뷰포트 */
  viewport?: string;
  /** ISO locale (ko-KR, en-US 등) */
  locale?: string;
  /** IANA timezone (Asia/Seoul 등) */
  timezone?: string;
  /** "dark" or "light" — 매 이벤트 시점의 테마 선호 */
  prefersColorScheme?: "dark" | "light";
}

function detectDeviceType(): "desktop" | "tablet" | "mobile" {
  if (typeof window === "undefined") return "desktop";
  const ua = navigator.userAgent || "";
  // tablet 우선 검사 (mobile 패턴 일부 포함)
  if (/iPad|Tablet|PlayBook|Silk|Android(?!.*Mobile)/i.test(ua)) return "tablet";
  if (/Mobile|iPhone|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)) return "mobile";
  return "desktop";
}

/**
 * 첫 landing 메타 캡처 (1회만 — sessionStorage 가 이미 있으면 skip).
 * 페이지 로드 시 한 번 호출.
 */
export function captureSessionMetaIfFirstVisit(): SessionMeta {
  if (typeof window === "undefined") return {};
  try {
    const existing = window.sessionStorage.getItem(SESSION_META_KEY);
    if (existing) return JSON.parse(existing);
  } catch { /* ignore */ }

  const meta: SessionMeta = {};
  // utm 파라미터
  try {
    const params = new URLSearchParams(window.location.search);
    for (const k of UTM_KEYS) {
      const v = params.get(k);
      if (v) meta[k] = v.slice(0, 128);
    }
  } catch { /* ignore */ }

  meta.landingReferrer = document.referrer ? document.referrer.slice(0, 256) : undefined;
  meta.landingPath = window.location.pathname || "/";
  meta.landingTs = Date.now();
  meta.initialViewport = `${window.innerWidth}x${window.innerHeight}`;
  meta.deviceType = detectDeviceType();

  try {
    window.sessionStorage.setItem(SESSION_META_KEY, JSON.stringify(meta));
  } catch { /* ignore */ }
  return meta;
}

/**
 * 현재 세션 메타 조회 (없으면 빈 객체).
 * captureSessionMetaIfFirstVisit() 를 먼저 호출했어야 채워져 있음.
 */
export function getSessionMeta(): SessionMeta {
  if (typeof window === "undefined") return {};
  try {
    const text = window.sessionStorage.getItem(SESSION_META_KEY);
    return text ? JSON.parse(text) : {};
  } catch {
    return {};
  }
}

/**
 * 매 이벤트마다 cheap 하게 캡처되는 메타.
 */
export function captureRequestMeta(): RequestMeta {
  if (typeof window === "undefined") return {};
  const meta: RequestMeta = {
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    locale: typeof navigator !== "undefined" ? navigator.language : undefined,
  };
  try {
    meta.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch { /* ignore */ }
  try {
    if (window.matchMedia) {
      meta.prefersColorScheme = window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
    }
  } catch { /* ignore */ }
  return meta;
}
